/**
 * V2 Log — photo-pick validation and the date helper. Split out of log.js on
 * 2026-07-25 (960 lines, plan budget ~400).
 *
 * Pure: no gateway, no DOM. The limits live here with the function that
 * enforces them, so the numbers and the rules cannot drift apart. log.js
 * re-exports validatePhotoPick and toDateInput, which the tests import.
 */

export const MAX_PHOTOS = 10; // matches V1
const MAX_FILE_SIZE_MB = 10; // per file, matches V1 create-yoink.js
const MAX_TOTAL_SIZE_MB = 50; // aggregate across all photos, matches V1

/**
 * V1's photo validation (create-yoink.js:1578-1606), same rules and
 * messages: image-only, 10MB per file, 50MB total, 10-photo cap.
 * Skipping this is how a bad file used to reach compressImage and hang
 * the save (M8.5 Tier 0). Exported for tests.
 * @returns {{ accepted: Array<{file: File}>, error: string }}
 */
export function validatePhotoPick(existing, files) {
  const maxTotalBytes = MAX_TOTAL_SIZE_MB * 1024 * 1024;
  let total = existing.reduce((sum, item) => sum + (item.file?.size || 0), 0);
  const accepted = [];
  let error = '';
  for (const file of files) {
    if (existing.length + accepted.length >= MAX_PHOTOS) {
      error = `Maximum ${MAX_PHOTOS} photos allowed.`;
      break;
    }
    if (!file.type.startsWith('image/')) {
      error = 'Only image files are allowed.';
      continue;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      error = `Each image must be ${MAX_FILE_SIZE_MB}MB or smaller.`;
      continue;
    }
    if (total + file.size > maxTotalBytes) {
      const currentMB = Math.round(total / 1024 / 1024);
      const fileMB = Math.round((file.size / 1024 / 1024) * 10) / 10;
      error = `Total upload size limit is ${MAX_TOTAL_SIZE_MB}MB. Current: ${currentMB}MB, this file: ${fileMB}MB.`;
      continue;
    }
    total += file.size;
    accepted.push({ file });
  }
  return { accepted, error };
}

/** yyyy-mm-dd in the browser's timezone — the same local date the "Yoinked" stamps display. */
export function toDateInput(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
